import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import { confirmAlert } from 'react-confirm-alert';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import Modal from 'react-modal';
import services from '@C/Admin/Users/_utils/data';
import { TableGrid } from '@C/TableGrid';
import {
  CountryList,
  DateFilter,
  DateRangeFilter,
  SubscriptionPlans,
} from '@C/Utils';
import DropLoaderLarge from '@C/Utils/DropLoaderLarge';
import { authHeader } from '@H';
import SimpleReactValidator from 'simple-react-validator';
import './styles.scoped.scss';

const enabledActions = [
  {
    action: 'Delete',
    roles: ['Site Admin'],
  },
];

const UserList = (props) => {
  const [dealerList, setDealerList] = useState([]);
  const [state, setState] = useState({
    from_date: props.cod
      ? props.date
      : dayjs().subtract(1, 'month').format('YYYY-MM-DD'),
    to_date: props.cod ? props.date : dayjs().format('YYYY-MM-DD'),
    bunk_name: '',
    first_name: '',
    last_name: '',
    city: '',
    state: '',
    country_id: '',
    mobile_number: '',
    created_by_id: '',
    executive_name: '',
    limit: 20,
    offset: 1,
    showModal: false,
    selectedUser: '',
    selectedBunkName: '',
  });
  const [loading, setLoading] = useState(true);
  const [count, setCount] = useState(0);
  const limitInput = useRef();

  const col = [
    {
      name: 'Account ID',
      key: 'account_id',
      noformat: true,
    },
    {
      name: 'Bunk register date',
      key: 'created_on',
      sortable: true,
      type: 'date',
    },
    {
      name: 'Bunk name',
      key: 'all',
      formatter(props) {
        return (
          <>
            <Link to={`/admin/users/bunk/${props.account_id}`}>
              {' '}
              {props.name}{' '}
            </Link>
          </>
        );
      },
    },
    {
      name: 'OMC',
      key: 'omc_name',
    },

    {
      name: 'State',
      key: 'state',
    },
    {
      name: 'City',
      key: 'city',
    },
    {
      name: 'Contact',
      key: 'contact',
    },
    {
      name: 'Plan name',
      key: 'subscription_package',
    },
    {
      name: 'Expiry date',
      key: 'all',
      formatter(props) {
        return (
          <>
            {props.subscription_expiry_date
              ? dayjs(props.subscription_expiry_date).format(
                  'DD/MM/YYYY HH:mm A',
                )
              : 'N/A'}
            {dayjs(props.subscription_expiry_date).isBefore(dayjs()) ? (
              <span className="--red --bold"> (Expired) </span>
            ) : (
              ''
            )}
            <button
              title="Manage subscription"
              className="btn btn-link"
              onClick={() =>
                setState((prev) => ({
                  ...prev,
                  showModal: true,
                  selectedUser: props.account_id,
                  selectedBunkName: props.name,
                }))
              }
            >
              <i className="fa-regular fa-file-invoice-dollar"></i>
            </button>
          </>
        );
      },
    },
  ];

  useEffect(() => {
    getDealersList();
    // eslint-disable-next-line
  }, []);

  const getDealersList = () => {
    setLoading(true);
    services
      .getDealersList({
        ...state,
      })
      .then((response) => {
        setDealerList(response.data.data.results);
        setLoading(false);
        setCount(response.data.data.count);
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
      });
  };

  const handlePagination = (e) => {
    setState((prev) => ({ ...prev, offset: e }));
    setLoading(true);
    setTimeout(() => {
      getDealersList();
    }, 0);
  };

  const confirmDelete = (item) => {
    confirmAlert({
      title: 'Confirm dealer account deletion',
      message: 'Are you sure',
      buttons: [
        {
          label: 'Yes',
          onClick: () => deleteItem(item),
        },
        {
          label: 'No',
          onClick: () => false,
        },
      ],
    });
  };

  const deleteItem = (item) => {
    const headers = authHeader(1);
    axios
      .delete(
        `${process.env.API_URL}/users/delete-dealer/?account_id=${item.account_id}`,
        {
          headers,
        },
      )
      .then(() => {
        toast.success('Dealer account deleted successfully', {
          position: 'bottom-center',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
        });
        getDealersList();
      });
  };

  const handleInputChange = (e) => {
    if (Array.isArray(e)) {
      const [from_date, to_date] = e;
      setState((prev) => ({
        ...prev,
        from_date: from_date || '',
        to_date: to_date || '',
      }));
      return;
    }

    const { name, value } = e.target;
    setState((prev) => ({ ...prev, [name]: value }));
  };

  const handleCountryChange = (e) => {
    setState((prev) => ({ ...prev, country_id: e?.value }));
  };

  const resetFilter = () => {
    setState({
      from_date: props.cod
        ? props.date
        : dayjs().subtract(1, 'month').format('YYYY-MM-DD'),
      to_date: props.cod ? props.date : dayjs().format('YYYY-MM-DD'),
      bunk_name: '',
      first_name: '',
      last_name: '',
      city: '',
      state: '',
      country_id: '',
      mobile_number: '',
      created_by_id: '',
      executive_name: '',
      limit: 20,
      offset: 1,
    });
    setLoading(true);
    setTimeout(() => {
      getDealersList();
    }, 0);
  };

  const submitForm = (e) => {
    e.preventDefault();
    setLoading(true);
    setTimeout(() => {
      getDealersList();
    }, 0);
  };

  const closeModal = () => {
    setState((prev) => ({
      ...prev,
      showModal: false,
      selectedUser: '',
      selectedBunkName: '',
    }));
  };

  return (
    <section
      className={`widget full-width list-table ${props.cod ? 'inverted' : ''}`}
    >
      <header>
        <h3> All Users List </h3>
      </header>
      <main>
        {!props.cod && (
          <form onSubmit={submitForm}>
            <div className="table-filters">
              <div className="filter-wrapper">
                <div className="filter-group with-icon">
                  <label>
                    Results to display- {state.limit || 20} (Total: {count})
                  </label>
                  <input
                    type="text"
                    name="limit"
                    onChange={handleInputChange}
                    value={state.limit}
                  />
                  <button type="submit" className="search-icon" />
                </div>
                <div className="filter-group">
                  <label>Date range:</label>
                  <DateRangeFilter
                    value={[state.from_date, state.to_date]}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="filter-group">
                  <label> Bunk Name </label>
                  <input
                    type="text"
                    value={state.bunk_name}
                    name="bunk_name"
                    onChange={handleInputChange}
                  />
                </div>
                <div className="filter-group">
                  <label> Mobile </label>
                  <input
                    type="text"
                    value={state.mobile_number}
                    name="mobile_number"
                    onChange={handleInputChange}
                  />
                </div>
                <div className="filter-group">
                  <label> City </label>
                  <input
                    type="text"
                    value={state.city}
                    name="city"
                    onChange={handleInputChange}
                  />
                </div>
                <div className="filter-group">
                  <label> State </label>
                  <input
                    type="text"
                    value={state.state}
                    name="state"
                    onChange={handleInputChange}
                  />
                </div>
                <div className="filter-group">
                  <label> Country </label>
                  <CountryList
                    handleCountryChange={handleCountryChange}
                    value={state.country_id}
                  />
                </div>
                <div className="filter-group">
                  <label> Executive name </label>
                  <input
                    type="text"
                    value={state.executive_name}
                    name="executive_name"
                    onChange={handleInputChange}
                  />
                </div>
                <div className="filter-group">
                  <button type="submit" className="blue-btn">
                    Search
                  </button>
                </div>
                <div className="filter-group reset-filter">
                  <button type="button" onClick={resetFilter}>
                    Reset filters
                  </button>
                </div>
              </div>
            </div>
          </form>
        )}

        <div className="table-wrapper">
          {loading ? (
            <DropLoaderLarge />
          ) : (
            <>
              <TableGrid
                col={col}
                row={dealerList}
                offset={state.offset}
                count={count}
                limit={state.limit}
                handlePagination={handlePagination}
                onEdit={props.onEdit}
                onDelete={confirmDelete}
                enabledActions={enabledActions}
              />
            </>
          )}
        </div>
        <Modal
          isOpen={state.showModal}
          onRequestClose={closeModal}
          contentLabel="Manage subscription modal"
          shouldCloseOnOverlayClick={true}
          className="sub-xl-modal-container"
          overlayClassName="xl-modal-overlay"
          parentSelector={() => document.querySelector('.page-components')}
          style={{
            content: {
              width: '50%',
            },
          }}
        >
          <div className="modal-component">
            <ManageSubscription
              userId={state.selectedUser}
              bunkName={state.selectedBunkName}
            />
          </div>
        </Modal>
      </main>
    </section>
  );
};

const ManageSubscription = (props) => {
  const [userId, setUserId] = useState(props.userId);
  const [bunkName] = useState(props.bunkName);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAddSubmitting, setIsAddSubmitting] = useState(false);
  const [validity, setValidity] = useState('');
  const [, forceUpdate] = useState();
  const [subscription_plan_id, setSubscriptionPlanId] = useState(null);
  const [timeStamp, setTimeStamp] = useState(dayjs().format('YYYY-MM-DD'));
  const [activation_date, setActivationDate] = useState(
    dayjs().format('YYYY-MM-DD'),
  );
  const simpleValidator = useRef(
    new SimpleReactValidator({ autoForceUpdate: { forceUpdate: forceUpdate } }),
  );

  const changePlanValidator = useRef(
    new SimpleReactValidator({ autoForceUpdate: { forceUpdate: forceUpdate } }),
  );

  useEffect(() => {
    fetchSubscriptionData();
  }, [props.userId]);

  const fetchSubscriptionData = () => {
    services
      .getUserSubscriptionDetails(userId)
      .then((response) => {
        console.log(response);
        setSubscriptionData(response.data.data);
        setLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
      });
  };

  const setExpiryLabel = (date) => {
    if (!date) return '';
    const expiryDate = dayjs(date);
    if (expiryDate.isBefore(dayjs())) {
      return (
        <span className="--red --bold">
          Expired on {expiryDate.format('DD-MM-YYYY HH:mm A')}
        </span>
      );
    } else {
      return <>Valid until {expiryDate.format('DD-MM-YYYY HH:mm A')}</>;
    }
  };

  const extendValidity = () => {
    if (simpleValidator.current.allValid()) {
      setIsSubmitting(true);
      let headers = authHeader(1);
      axios
        .post(
          `${process.env.API_URL}/subscription/subscription-extension/`,
          {
            subscription_id: subscriptionData.subscription_id,
            days: validity,
            time_stamp: timeStamp,
          },
          { headers: headers },
        )
        .then((response) => {
          toast.success('Subscription extended successfully', {
            position: 'bottom-center',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
          });
          fetchSubscriptionData();
          setValidity('');
          setIsSubmitting(false);
        })
        .catch((error) => {
          console.log(error);
          setIsSubmitting(false);
        });
    } else {
      simpleValidator.current.showMessages();
      forceUpdate(1);
    }
  };

  const changePlan = () => {
    if (changePlanValidator.current.allValid()) {
      setIsAddSubmitting(true);
      let headers = authHeader(1);
      axios
        .post(
          `${process.env.API_URL}/subscription/add/`,
          {
            subscription_plan_id: subscription_plan_id,
            bunk_id: userId,
            activation_date: activation_date,
          },
          { headers: headers },
        )
        .then((response) => {
          toast.success('Subscription plan updated successfully');
          fetchSubscriptionData();
          setValidity('');
          setIsAddSubmitting(false);
        })
        .catch((error) => {
          console.log(error);
          setIsAddSubmitting(false);
        });
    } else {
      simpleValidator.current.showMessages();
      forceUpdate(1);
    }
  };

  return (
    <section className="widget">
      <header>
        <h3> Manage subscription </h3>
      </header>
      <main>
        {loading ? (
          <DropLoaderLarge />
        ) : (
          <div className="subscription-details">
            <div className="subscription-panel">
              <h3> Current Plan </h3>
              <p>
                Bunk: <span>{bunkName}</span>
              </p>
              <p>
                Package:{' '}
                <span>{subscriptionData?.package?.plan_name}</span>{' '}
              </p>
              <p>
                Plan: <span>{subscriptionData?.name}</span>{' '}
              </p>
              <p>
                Duration: <span>{subscriptionData?.validity} days</span>{' '}
              </p>
              <p>
                Expiry:{' '}
                <span>
                  {setExpiryLabel(subscriptionData?.subscription_expiry_date)}
                </span>{' '}
              </p>
            </div>
            <div className="subscription-panel">
              <h3> Extend plan validity </h3>
              <form>
                <div className="input-wrapper with-label">
                  <label>Activation date</label>
                  <DateFilter
                    name="time_stamp"
                    value={timeStamp}
                    onChange={(e) => setTimeStamp(e.target.value)}
                  />
                </div>
                <div className="input-wrapper with-label">
                  <label className="label">Days</label>
                  {simpleValidator.current.message(
                    'validity',
                    validity,
                    'required',
                  )}
                  <input
                    type="text"
                    value={validity}
                    onChange={(e) => setValidity(e.target.value)}
                  />
                </div>
                <div className="form-actions">
                  <button
                    className={`green-btn ${isSubmitting ? 'loading' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      extendValidity();
                    }}
                  >
                    Extend
                  </button>
                </div>
              </form>
            </div>
            <div className="subscription-panel">
              <h3> Change plan</h3>
              <div className="form-group flex flex-row gap-10 p-10 justify-between">
                <div className="input-wrapper with-label with-btn mb-0 flex-1">
                  <label>Plan</label>
                  {changePlanValidator.current.message(
                    'subscription_plan_id',
                    subscription_plan_id,
                    'required',
                  )}
                  <SubscriptionPlans
                    handlePackageChange={(e) => {
                      setSubscriptionPlanId(e?.value);
                    }}
                    value={subscription_plan_id}
                  />
                </div>
                <div className="input-wrapper with-label mb-0 flex-1">
                  <label>Activation date</label>
                  <DateFilter
                    name="activation_date "
                    value={activation_date}
                    onChange={(e) => setActivationDate(e.target.value)}
                  />
                </div>
              </div>
              <button
                className={`green-btn ${isAddSubmitting ? 'loading' : ''} flex-1`}
                onClick={(e) => {
                  e.preventDefault();
                  changePlan();
                }}
              >
                Submit
              </button>
            </div>
          </div>
        )}
      </main>
    </section>
  );
};

export { UserList };
